// ============================================================
// HoverChat Admin — Login Page
// Two options: email + password, or "email me a magic link".
// ============================================================

import { useState } from 'react';
import { useAuth } from '../../App';
import { api } from '../../lib/api';
import { MessageSquare, Mail, Lock } from 'lucide-react';

type Mode = 'magic' | 'password';

export function LoginPage() {
  const { login } = useAuth();
  const [mode, setMode] = useState<Mode>('magic');

  // Shared
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  // Password mode
  const [password, setPassword] = useState('');

  // Magic-link mode
  const [magicSent, setMagicSent] = useState<string | null>(null);

  const handlePasswordLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
    try {
      await login(email, password);
    } catch (err: any) {
      setError(err.message || 'Invalid email or password');
    } finally {
      setIsLoading(false);
    }
  };

  const handleMagicRequest = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
    try {
      const result = await api.requestMagicLink(email);
      setMagicSent(result.message);
    } catch (err: any) {
      setError(err.message || 'Could not send sign-in link');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-primary/10 rounded-2xl mb-4">
            <MessageSquare className="w-8 h-8 text-primary" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900">HoverChat Admin</h1>
          <p className="text-gray-500 mt-1">Sign in to manage your chatbots</p>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8">
          {magicSent ? (
            <div className="space-y-4 text-center">
              <div className="inline-flex items-center justify-center w-12 h-12 bg-green-100 rounded-full">
                <Mail className="w-6 h-6 text-green-600" />
              </div>
              <h2 className="text-lg font-semibold text-gray-900">Check your inbox</h2>
              <p className="text-sm text-gray-600">{magicSent}</p>
              <button
                type="button"
                onClick={() => {
                  setMagicSent(null);
                  setEmail('');
                }}
                className="text-sm text-primary font-medium hover:underline"
              >
                Use a different email
              </button>
            </div>
          ) : (
            <>
              {/* Mode toggle */}
              <div className="flex gap-1 p-1 bg-gray-100 rounded-lg mb-6 text-xs font-medium">
                <button
                  type="button"
                  onClick={() => { setMode('magic'); setError(''); }}
                  className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-md transition-colors ${
                    mode === 'magic' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  <Mail className="w-3.5 h-3.5" /> Email me a link
                </button>
                <button
                  type="button"
                  onClick={() => { setMode('password'); setError(''); }}
                  className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-md transition-colors ${
                    mode === 'password' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  <Lock className="w-3.5 h-3.5" /> Password
                </button>
              </div>

              <form onSubmit={mode === 'magic' ? handleMagicRequest : handlePasswordLogin} className="space-y-5">
                {error && (
                  <div className="bg-red-50 text-red-600 text-sm px-4 py-3 rounded-lg border border-red-200">
                    {error}
                  </div>
                )}

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1.5">
                    Email address
                  </label>
                  <input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    autoFocus
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all text-sm"
                    placeholder="you@hovercam.com"
                  />
                </div>

                {mode === 'password' && (
                  <div>
                    <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1.5">
                      Password
                    </label>
                    <input
                      id="password"
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all text-sm"
                      placeholder="Enter your password"
                    />
                  </div>
                )}

                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full bg-primary text-white py-2.5 rounded-lg font-medium text-sm hover:bg-primary/90 focus:ring-2 focus:ring-primary/20 focus:ring-offset-2 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoading
                    ? mode === 'magic' ? 'Sending link…' : 'Signing in…'
                    : mode === 'magic' ? 'Send sign-in link' : 'Sign In'}
                </button>

                {mode === 'magic' && (
                  <p className="text-xs text-gray-500 text-center leading-relaxed">
                    Any @hovercam.com email can request a link. First-time users are provisioned automatically.
                  </p>
                )}
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
